import { useTranslation } from 'react-i18next';
import { formatAnalyticsSyncDate } from '@/lib/social-analytics/formatAnalyticsSyncDate';
import type { AnalyticsConnection } from './AccountFilters';

export type AnalyticsRefreshResult = {
  connection_id: string;
  status: string;
  error_code?: string | null;
  next_allowed_at?: string | null;
};

type AnalyticsRefreshFeedbackProps = {
  connections: readonly AnalyticsConnection[];
  results: readonly AnalyticsRefreshResult[];
  now: number;
  dateLocale: string;
};

const futureTimestamp = (value: string | null | undefined, now: number) => {
  const timestamp = value ? Date.parse(value) : NaN;
  return Number.isFinite(timestamp) && timestamp > now ? timestamp : null;
};

const refreshResultTranslationKey = (result: AnalyticsRefreshResult) => {
  if (result.error_code === 'social_analytics_disabled') return 'featureDisabled';
  if (result.error_code === 'social_analytics_queue_failed') return 'queueFailed';
  return ['queued', 'already_running', 'cooldown', 'reconnect_required', 'unsupported'].includes(result.status)
    ? result.status
    : 'unavailable';
};

export const AnalyticsRefreshFeedback = ({ connections, results, now, dateLocale }: AnalyticsRefreshFeedbackProps) => {
  const { t } = useTranslation();
  if (results.length === 0) return null;

  return (
    <ul aria-live="polite" className="space-y-1 rounded-lg border border-border/70 bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
      {results.map((result) => {
        const connection = connections.find(({ id }) => id === result.connection_id);
        const account = connection ? `@${connection.username}` : t('analytics.refresh.account');
        const cooldown = result.status === 'cooldown' ? futureTimestamp(result.next_allowed_at, now) : null;
        const status = cooldown
          ? t('analytics.refresh.results.cooldownUntil', {
            date: formatAnalyticsSyncDate(
              new Date(cooldown).toISOString(),
              dateLocale,
              t('analytics.refresh.results.cooldown'),
            ),
          })
          : t(`analytics.refresh.results.${refreshResultTranslationKey(result)}`);
        return <li key={result.connection_id}><span className="font-medium text-foreground">{account}</span> · {status}</li>;
      })}
    </ul>
  );
};
